import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { MaterialNames } from "@/components/material_symbol_names";
import { Path } from "@/router";
import { ProgramTypeName } from "@/types";

const PGM_GRADUATE_TERMINATION_REASON = "pgm_graduate";

type PgmUniversity = {
  legacyId?: string | null;
  name?: string | null;
};

type PgmUniversityMetricPerson = {
  pgaId?: string | null;
  lastAttendedPgmUniversity?: PgmUniversity | null;
  educationLatestPgmProgram?: { pgmProgramCompleted?: boolean | null } | null;
  bands?: Array<{
    terminatedReason?: string | null;
    programType?: { name?: string | null } | null;
  }> | null;
};

/**
 * Alumni roster membership matches backend universityStudents(alumni: true):
 * approved graduation / completed PGM program, or a student band terminated as pgm_graduate.
 */
export function isPgmUniversityGraduate(person: PgmUniversityMetricPerson): boolean {
  if (person.educationLatestPgmProgram?.pgmProgramCompleted === true) {
    return true;
  }

  return (person.bands ?? []).some(
    (band) =>
      band.programType?.name === ProgramTypeName.Student && band.terminatedReason === PGM_GRADUATE_TERMINATION_REASON
  );
}

/**
 * Shows last-attended PGM university. Links to the alumni roster only for graduates;
 * withdrawals / non-alumni get display-only (current roster would 404).
 * Graduate links pre-fill `searchTerm` with the person's PGA ID.
 */
export function buildPgmUniversityMetric(person: PgmUniversityMetricPerson): MetricCardType<Path> | null {
  const university = person.lastAttendedPgmUniversity;
  const legacyId = university?.legacyId?.trim();
  const name = university?.name?.trim();
  const pgaId = person.pgaId?.trim();
  if (!legacyId || !name) return null;

  const alumniPath = "/education/universities/:universityId/students/alumni" as const;

  if (!isPgmUniversityGraduate(person) || !pgaId) {
    return {
      label: "PGM University",
      value: name,
      iconName: "school" as MaterialNames
    };
  }

  return {
    label: "PGM University",
    value: name,
    iconName: "school" as MaterialNames,
    link: {
      to: {
        pathname: alumniPath,
        search: `searchTerm=${encodeURIComponent(pgaId)}`
      },
      params: { universityId: legacyId }
    }
  } as unknown as MetricCardType<Path>;
}
